import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import Layout from '@/components/Layout';
import { RankTrackCard } from '@/components/ranks/RankTrackCard';
import { SeasonTitlesList } from '@/components/ranks/SeasonTitlesList';
import { RankHall } from '@/components/ranks/RankHall';
import { useMyClipperRanksQuery, useClipperRankHallQuery } from '@/generated/graphql';
import { CLIPPER_RANK_HALL_VIEWER } from '@/queries/ranks';
import { accumulateSeasonOptions, type SeasonOption } from '@/lib/ranks/accumulateSeasonOptions';

const HALL_PAGE_SIZE = 50;
const TRACK_SKELETON_COUNT = 5;
const HALL_SKELETON_ROWS = 10;

function TrackGridSkeleton() {
  return (
    <section data-testid="clipper-ranks-yours-skeleton" className="space-y-4">
      <div className="h-7 w-40 rounded bg-muted animate-pulse" />
      <div
        data-testid="clipper-ranks-track-grid"
        className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      >
        {Array.from({ length: TRACK_SKELETON_COUNT }).map((_, index) => (
          <div key={index} className="rounded-xl border border-border bg-card p-4 space-y-3 animate-pulse">
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="h-3 w-16 rounded bg-muted" />
            <div className="h-16 w-16 rounded-full bg-muted" />
            <div className="h-3 w-full rounded bg-muted" />
            <div className="h-2 w-full rounded bg-muted" />
          </div>
        ))}
      </div>
    </section>
  );
}

function HallSkeleton() {
  return (
    <section data-testid="clipper-ranks-hall-skeleton" className="space-y-4">
      <div className="h-7 w-36 rounded bg-muted animate-pulse" />
      <div className="h-10 w-64 rounded-md bg-muted animate-pulse" />
      <div className="rounded-xl border border-primary/25 overflow-hidden bg-card">
        {Array.from({ length: HALL_SKELETON_ROWS }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 py-2.5 px-3 border-b border-primary/10 last:border-b-0"
          >
            <div className="h-4 w-8 rounded bg-muted animate-pulse" />
            <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
            <div className="h-4 flex-1 rounded bg-muted animate-pulse" />
            <div className="h-4 w-16 rounded bg-muted animate-pulse" />
          </div>
        ))}
      </div>
    </section>
  );
}

const ClipperRanksPage = () => {
  const { t } = useTranslation();
  const [seasonId, setSeasonId] = useState<string | undefined>(undefined);
  const [seasonOptions, setSeasonOptions] = useState<SeasonOption[]>([]);

  const {
    data: ranksData,
    error: ranksError,
    loading: ranksLoading,
  } = useMyClipperRanksQuery();
  const { data: viewerData } = useQuery(CLIPPER_RANK_HALL_VIEWER);

  const {
    data: hallData,
    error: hallError,
    loading: hallLoading,
  } = useClipperRankHallQuery({
    variables: {
      ...(seasonId ? { seasonId } : {}),
      offset: 0,
      limit: HALL_PAGE_SIZE,
    },
  });

  const ranks = ranksData?.myClipperRanks;
  const hall = hallData?.clipperRankHall;
  const viewerMe = viewerData?.myData;
  const viewer = viewerMe
    ? {
      user_id: viewerMe.id,
      name: viewerMe.name,
      avatar_url: viewerMe.profile_picture ?? null,
    }
    : null;

  // The hall only ever returns the selected season, so the options have to
  // accumulate: rebuilding them from the current response drops the live
  // season as soon as a past one is picked.
  useEffect(() => {
    setSeasonOptions((previous) => accumulateSeasonOptions(previous, [
      hall?.season ? { id: hall.season.id, name: hall.season.name } : null,
      ...(ranks?.season_titles ?? []).map((title) => ({
        id: title.season_id,
        name: title.season_name,
      })),
    ]));
  }, [hall?.season, ranks?.season_titles]);

  if (ranksError || hallError) {
    return (
      <Layout>
        <div className="min-h-screen bg-background p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm text-destructive">{t('ranks.loadError')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const showRanksSkeleton = ranksLoading && !ranks;
  const showHallSkeleton = hallLoading && !hall;

  return (
    <Layout>
      <div className="min-h-screen bg-background p-4 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold">{t('ranks.title')}</h1>

          {showRanksSkeleton ? <TrackGridSkeleton /> : null}
          {ranks && (
            <section data-testid="clipper-ranks-yours" className="space-y-4">
              <h2 className="text-xl font-semibold">{t('ranks.yours')}</h2>
              <div
                data-testid="clipper-ranks-track-grid"
                className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
              >
                {ranks.tracks.map((track) => (
                  <RankTrackCard key={track.id} track={track} />
                ))}
              </div>
              <SeasonTitlesList titles={ranks.season_titles} />
            </section>
          )}

          {showHallSkeleton ? <HallSkeleton /> : null}
          {hall && (
            <section data-testid="clipper-ranks-hall" className="space-y-4">
              <h2 className="text-xl font-semibold">{t('ranks.hall')}</h2>
              <RankHall
                season={hall.season}
                clippers={hall.clippers}
                seasonOptions={seasonOptions}
                selectedSeasonId={seasonId}
                onSeasonChange={setSeasonId}
                viewer={viewer}
              />
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClipperRanksPage;
